
import React, { useState, useEffect, useRef } from "react";
import { Partnership } from "@/Entities/Partnership";
import AnalyticsReportService from "@/Entities/AnalyticsReportService.js";
import { Card, CardContent, CardHeader, CardTitle } from "@/Components/ui/card";
import { Button } from "@/Components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/Components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, Sankey } from "recharts";
import { FileText, Download, DollarSign, TrendingUp, Building2, AlertTriangle, CheckCircle, MapPin, Calendar, HelpCircle } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { calculateDefaultRate, getDefaultRatePeriodDescription, getDefaultRateInfo } from "@/utils/inadimplenciaCalculator";

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

// O ResponsiveContainer do Recharts começa medindo o container como {width:-1, height:-1}
// até o ResizeObserver reportar o tamanho real (isso é inerente à biblioteca, acontece
// sempre na 1ª renderização de qualquer gráfico). Um tamanho inicial positivo evita o aviso
// "width(-1) and height(-1)..." e, no Sankey em especial, evita que o algoritmo de layout
// calcule NaN para x/y/altura dos nós nesse primeiro instante (dividir por -1 gera NaN).
const CHART_INITIAL_DIMENSION = { width: 500, height: 300 };

// Cores específicas por status
const STATUS_COLORS = {
  'quitado': '#10B981',    // Verde
  'no_prazo': '#3B82F6',   // Azul
  'em_debito': '#EF4444',  // Vermelho
  'verificar': '#F59E0B'   // Amarelo
};

/**
 * Botão de ajuda reutilizável para cards de gráfico — explica a regra de
 * clique/fixação do balão de dados (ver ChartClickTooltip). Mesmo padrão
 * hover=prévia / clique=fixa já usado no card de Taxa de Inadimplência.
 */
function ChartHelpButton() {
  const [open, setOpen] = useState(false); // false | 'hover' | 'fixed'

  useEffect(() => {
    if (open !== 'fixed') return;
    const handleClickOutside = () => setOpen(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  return (
    <div className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        onMouseEnter={(e) => { e.stopPropagation(); if (!open) setOpen('hover'); }}
        onMouseLeave={(e) => { e.stopPropagation(); if (open === 'hover') setOpen(false); }}
        onClick={(e) => { e.stopPropagation(); setOpen(prev => prev === 'fixed' ? false : 'fixed'); }}
        className="text-slate-400 hover:text-slate-600 transition-colors"
        aria-label="Como consultar os dados deste gráfico"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-6 z-[100] w-72 p-3 bg-slate-900 text-white rounded-lg shadow-2xl border border-slate-700 text-xs space-y-1.5"
          style={{ maxWidth: 'calc(100vw - 2rem)' }}
          onMouseEnter={(e) => { e.stopPropagation(); if (open === 'hover') setOpen('hover'); }}
          onMouseLeave={(e) => { e.stopPropagation(); if (open === 'hover') setOpen(false); }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-bold">Como consultar os dados</p>
          <p className="text-slate-300">Clique num item do gráfico para ver o valor exato — o balão fecha sozinho em 6 segundos.</p>
          <p className="text-slate-300">Clique de novo no <strong className="text-white">mesmo item</strong> para fixar o balão aberto.</p>
          <p className="text-slate-300">Com o balão fixo, clique uma <strong className="text-white">terceira vez</strong> (ou recarregue a página) para fechá-lo.</p>
          <p className="text-slate-300">Só um balão fica aberto por vez — clicar em outro item substitui o atual.</p>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [partnerships, setPartnerships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('Todos os Anos');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showDefaultRateTooltip, setShowDefaultRateTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState('right'); // 'right' ou 'left'

  // Balão de dados por clique, compartilhado entre todos os gráficos da página —
  // só um item pode estar com o balão aberto/fixo por vez em toda a tela.
  // { chartId, itemKey, pinned, x, y, title, rows } | null
  const [chartTooltip, setChartTooltip] = useState(null);
  const chartTooltipTimerRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(chartTooltipTimerRef.current);
  }, []);

  /**
   * Regra de interação (ver ChartHelpButton para o texto exibido ao usuário):
   * 1º clique num item -> abre o balão, fecha sozinho em 6s se nada mais acontecer.
   * 2º clique no MESMO item -> fixa o balão (cancela o fechamento automático).
   * 3º clique no MESMO item (já fixo) -> fecha o balão.
   * Clique em item DIFERENTE (de qualquer gráfico) -> fecha o atual e abre o novo como 1º clique.
   */
  const handleChartItemClick = (chartId, itemKey, content, event) => {
    event?.stopPropagation?.();
    const x = event?.clientX ?? window.innerWidth / 2;
    const y = event?.clientY ?? window.innerHeight / 2;

    setChartTooltip(prev => {
      const isSameItem = prev && prev.chartId === chartId && prev.itemKey === itemKey;

      if (isSameItem && prev.pinned) {
        clearTimeout(chartTooltipTimerRef.current);
        return null;
      }

      if (isSameItem && !prev.pinned) {
        clearTimeout(chartTooltipTimerRef.current);
        return { ...prev, pinned: true };
      }

      clearTimeout(chartTooltipTimerRef.current);
      chartTooltipTimerRef.current = setTimeout(() => {
        setChartTooltip(current =>
          current && current.chartId === chartId && current.itemKey === itemKey && !current.pinned
            ? null
            : current
        );
      }, 6000);

      return { chartId, itemKey, pinned: false, x, y, ...content };
    });
  };

  // Fechar tooltip ao clicar fora (apenas quando fixo)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDefaultRateTooltip === 'fixed') {
        setShowDefaultRateTooltip(false);
      }
    };

    if (showDefaultRateTooltip === 'fixed') {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDefaultRateTooltip]);

  // Mapeamento bidirecional para anos
  const yearOptions = [
    { label: 'Todos os Anos', value: 'Todos os Anos', dbValue: 'all' }
  ];

  // Função para converter português → valor técnico
  const getDbValueFromYear = (yearLabel) => {
    if (yearLabel === 'Todos os Anos') return 'all';
    return yearLabel; // Anos específicos mantêm o valor
  };

  useEffect(() => {
    loadPartnerships();
  }, []);

  const loadPartnerships = async () => {
    setIsLoading(true);
    try {
      // Buscar todas as parcerias para relatórios (sem paginação)
      const response = await Partnership.listAll();
      // Verificar se response tem a estrutura de paginação
      const data = response.data || response;
      setPartnerships(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
      setPartnerships([]);
    }
    setIsLoading(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const getFilteredPartnerships = () => {
    if (!Array.isArray(partnerships)) return [];
    const yearDbValue = getDbValueFromYear(selectedYear);
    if (yearDbValue === 'all') return partnerships;
    return partnerships.filter(p => p.ano?.toString() === selectedYear);
  };

  const getAvailableYears = () => {
    if (!Array.isArray(partnerships)) return [];
    const years = [...new Set(partnerships.map(p => p.ano).filter(Boolean))];
    return years.sort((a, b) => b - a);
  };

  const getStatusDistribution = () => {
    const filtered = getFilteredPartnerships();
    const statusCount = filtered.reduce((acc, p) => {
      const status = p.status || 'indefinido';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const statusLabels = {
      'quitado': 'Quitado',
      'no_prazo': 'No Prazo',
      'em_debito': 'Em Débito',
      'verificar': 'Verificar',
      'indefinido': 'Indefinido'
    };

    return Object.entries(statusCount)
      .map(([status, count]) => ({
        name: statusLabels[status] || 'Indefinido',
        value: count,
        color: STATUS_COLORS[status] || '#6B7280',
        percentage: ((count / filtered.length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value); // Ordenar por valor decrescente
  };

  const getValueByYear = () => {
    const yearValues = partnerships.reduce((acc, p) => {
      // Ensure p.createdAt is a valid date before using getFullYear
      const date = (p.createdAt || p.created_at) ? (typeof (p.createdAt || p.created_at) === 'string' ? parseISO(p.createdAt || p.created_at) : new Date(p.createdAt || p.created_at)) : null;
      const year = p.ano || (date && isValid(date) ? date.getFullYear() : 'N/A'); // Fallback to 'N/A' if date is invalid

      if (year === 'N/A') return acc; // Skip if year cannot be determined

      const rawValue = p.valor ?? 0;
      const numericValue = Number(rawValue) || 0;
      acc[year] = (acc[year] || 0) + numericValue;
      return acc;
    }, {});

    return Object.entries(yearValues)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([year, value]) => ({
        year: year.toString(),
        value: value,
        formattedValue: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 0
        }).format(value)
      }));
  };


  const getTopOSCs = () => {
    const filtered = getFilteredPartnerships();
    const oscValues = filtered.reduce((acc, p) => {
      const osc = p.osc || 'Não informado';
      const numericValue = Number(p.valor) || 0;
      acc[osc] = (acc[osc] || 0) + numericValue;
      return acc;
    }, {});

    return Object.entries(oscValues)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([osc, value]) => ({
        osc: osc, // Nome completo mantido para o tooltip
        oscShort: osc.length > 30 ? osc.substring(0, 30) + '...' : osc, // Versão curta para o eixo Y
        value,
        formattedValue: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 0
        }).format(value)
      }));
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const generateAnalyticsPDF = async () => {
    if (isGeneratingPDF) return;
    
    setIsGeneratingPDF(true);
    
    try {
      await AnalyticsReportService.generatePDF(selectedYear);
      setIsGeneratingPDF(false);
      showNotification('Relatório Analítico gerado com sucesso!', 'success');
    } catch (error) {
      setIsGeneratingPDF(false);
      showNotification(`Erro ao gerar relatório: ${error.message}`, 'error');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Novos KPIs
  const getPartnershipsByType = () => {
    const filtered = getFilteredPartnerships();
    const typeCount = filtered.reduce((acc, p) => {
      const type = p.tipo_parceria || 'Não informado';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(typeCount)
      .map(([type, count]) => ({
        name: type.length > 25 ? type.substring(0, 25) + '...' : type,
        value: count
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getPartnershipsByOrigin = () => {
    const filtered = getFilteredPartnerships();
    const originCount = filtered.reduce((acc, p) => {
      const origin = p.origem || 'Não informado';
      acc[origin] = (acc[origin] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(originCount)
      .map(([origin, count]) => ({
        name: origin,
        value: count,
        percentage: ((count / filtered.length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getValueByType = () => {
    const filtered = getFilteredPartnerships();
    const typeValues = filtered.reduce((acc, p) => {
      const type = p.tipo_parceria || 'Não informado';
      const value = Number(p.valor) || 0;
      acc[type] = (acc[type] || 0) + value;
      return acc;
    }, {});

    return Object.entries(typeValues)
      .map(([type, value]) => ({
        name: type.length > 20 ? type.substring(0, 20) + '...' : type,
        value
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  };

  const getTopLocations = () => {
    const filtered = getFilteredPartnerships();
    const locationCount = filtered.reduce((acc, p) => {
      const location = p.local_f || 'Não informado';
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(locationCount)
      .map(([location, count]) => ({
        location: location.length > 30 ? location.substring(0, 30) + '...' : location,
        count,
        percentage: ((count / filtered.length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const getDefaultRate = () => {
    return calculateDefaultRate(partnerships, selectedYear);
  };

  const getCompletionRate = () => {
    const filtered = getFilteredPartnerships();
    const completed = filtered.filter(p => p.status === 'quitado').length;
    return ((completed / (filtered.length || 1)) * 100).toFixed(1);
  };

  const filtered = getFilteredPartnerships();
  const totalValue = filtered.reduce((sum, p) => sum + Number(p.valor) || 0, 0);

  /**
   * Monta os dados no formato exigido pelo Sankey: um nó "Total" fluindo para
   * um nó por categoria, com a largura do fluxo proporcional ao valor.
   * Categorias com valor zero são descartadas — um link de valor 0 quebra o
   * algoritmo de layout do Sankey (divisão por zero ao escalar as larguras).
   */
  const getValueByTypeSankeyData = () => {
    const typeData = getValueByType().filter((t) => t.value > 0);
    return {
      nodes: [{ name: 'Total' }, ...typeData.map((t) => ({ name: t.name }))],
      links: typeData.map((t, i) => ({ source: 0, target: i + 1, value: t.value }))
    };
  };

  // Altura/espessura mínima visual para nós e fluxos — sem isso, categorias com
  // valor muito menor que as demais (ordens de grandeza de diferença, comum aqui:
  // uma categoria pode responder por 99%+ do total) ficam com menos de 1px de altura
  // calculada e desaparecem completamente do diagrama, mesmo com dado correto por trás.
  const SANKEY_MIN_NODE_HEIGHT = 8;
  const SANKEY_MIN_LINK_WIDTH = 3;

  const renderValueByTypeSankeyNode = (nodeProps) => {
    const { x, y, width, height, index, payload } = nodeProps;
    const isTotalNode = index === 0;
    const color = isTotalNode ? '#64748b' : COLORS[(index - 1) % COLORS.length];
    const displayHeight = Math.max(height, SANKEY_MIN_NODE_HEIGHT);
    const displayY = y + height / 2 - displayHeight / 2;
    return (
      <g
        style={{ cursor: isTotalNode ? 'default' : 'pointer' }}
        onClick={(e) => {
          if (isTotalNode) return;
          const item = getValueByType().find((t) => t.name === payload.name);
          if (!item) return;
          handleChartItemClick('value-by-type', item.name, {
            title: item.name,
            rows: [{ label: formatCurrency(item.value), color }]
          }, e);
        }}
      >
        <rect x={x} y={displayY} width={width} height={displayHeight} fill={color} rx={2} />
        <text
          x={isTotalNode ? x - 8 : x + width + 8}
          y={displayY + displayHeight / 2}
          textAnchor={isTotalNode ? 'end' : 'start'}
          dominantBaseline="middle"
          fontSize={12}
          fontWeight={isTotalNode ? 600 : 500}
          fill="#334155"
        >
          {payload.name}
        </text>
      </g>
    );
  };

  const renderValueByTypeSankeyLink = (linkProps) => {
    const { sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, index } = linkProps;
    const color = COLORS[index % COLORS.length];
    return (
      <path
        d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke={color}
        strokeOpacity={0.55}
        strokeWidth={Math.max(linkWidth, SANKEY_MIN_LINK_WIDTH)}
      />
    );
  };


  return (
    <div className="p-3 sm:p-4 md:p-5 bg-slate-50 min-h-screen">
      {/* Notificação - Toast na parte inferior */}
      {notification && (
        <div className={`fixed bottom-6 right-6 z-50 p-4 rounded-lg shadow-xl border-l-4 max-w-md transform transition-all duration-300 animate-slide-up ${
          notification.type === 'success' ? 'bg-green-50 border-green-500 text-green-800' :
          notification.type === 'error' ? 'bg-red-50 border-red-500 text-red-800' :
          'bg-blue-50 border-blue-500 text-blue-800'
        }`}>
          <div className="flex items-center space-x-3">
            {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {notification.type === 'error' && <AlertTriangle className="w-5 h-5" />}
            {notification.type === 'info' && <FileText className="w-5 h-5" />}
            <span className="font-medium text-sm">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Gráficos Analíticos</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">
              Visualize dados e tendências das suas parcerias
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder={selectedYear || 'Selecione o Ano'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Todos os Anos">Todos os Anos</SelectItem>
                {getAvailableYears().map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={generateAnalyticsPDF}
              disabled={isGeneratingPDF}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto whitespace-nowrap"
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{isGeneratingPDF ? 'Gerando PDF...' : 'Gerar Relatório Analítico'}</span>
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          <Card className="relative overflow-hidden border-l-4 border-l-blue-500">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-5 rounded-full transform translate-x-8 -translate-y-8" />
            <CardHeader className="pb-3 px-4">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-600 mb-1">Total de Parcerias</p>
                  <p className="text-3xl lg:text-xl xl:text-2xl font-bold text-slate-900 break-words">
                    {filtered.length}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedYear === 'Todos os Anos' ? 'Todos os períodos' : `Ano de ${selectedYear}`}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="relative overflow-hidden border-l-4 border-l-green-500">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500 opacity-5 rounded-full transform translate-x-8 -translate-y-8" />
            <CardHeader className="pb-3 px-4">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-600 mb-1">Valor Total</p>
                  <p className="text-3xl lg:text-xl xl:text-2xl font-bold text-slate-900 break-words">
                    {formatCurrency(totalValue)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Soma de todas as parcerias
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="relative overflow-hidden border-l-4 border-l-purple-500">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500 opacity-5 rounded-full transform translate-x-8 -translate-y-8" />
            <CardHeader className="pb-3 px-4">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-600 mb-1">Valor Médio</p>
                  <p className="text-3xl lg:text-xl xl:text-2xl font-bold text-slate-900 break-words">
                    {formatCurrency(totalValue / (filtered.length || 1))}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Por parceria
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-xl flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="relative border-l-4 border-l-red-500" style={{ overflow: 'visible' }}>
            {/* Wrapper próprio para o círculo decorativo: a Card precisa de overflow
                visible para o tooltip de ajuda escapar do card, então o círculo não
                pode mais depender do overflow-hidden da Card (como nos outros 3 cards)
                — ganha um clipping independente que acompanha o formato do card. */}
            <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500 opacity-5 rounded-full transform translate-x-8 -translate-y-8" />
            </div>
            <CardHeader className="pb-3 px-4">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-slate-600">Taxa de Inadimplência</p>
                    <div className="relative">
                      <button
                        ref={(el) => {
                          if (el && showDefaultRateTooltip) {
                            // Calcular posição do tooltip
                            const rect = el.getBoundingClientRect();
                            const tooltipWidth = 384; // w-96 = 384px
                            const spaceRight = window.innerWidth - rect.right;
                            const spaceLeft = rect.left;
                            
                            // Se não houver espaço à direita, mostrar à esquerda
                            if (spaceRight < tooltipWidth + 24 && spaceLeft > tooltipWidth + 24) {
                              setTooltipPosition('left');
                            } else {
                              setTooltipPosition('right');
                            }
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          if (!showDefaultRateTooltip) {
                            setShowDefaultRateTooltip('hover');
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.stopPropagation();
                          if (showDefaultRateTooltip === 'hover') {
                            setShowDefaultRateTooltip(false);
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDefaultRateTooltip(prev => prev === 'fixed' ? false : 'fixed');
                        }}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      
                      {showDefaultRateTooltip && (
                        <div 
                          className={`absolute ${tooltipPosition === 'right' ? 'left-6' : 'right-6'} top-0 z-[100] w-96 p-4 bg-slate-900 text-white rounded-lg shadow-2xl border border-slate-700`}
                          style={{
                            maxWidth: 'calc(100vw - 2rem)',
                          }}
                          onMouseEnter={(e) => {
                            e.stopPropagation();
                            if (showDefaultRateTooltip === 'hover') {
                              setShowDefaultRateTooltip('hover');
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.stopPropagation();
                            if (showDefaultRateTooltip === 'hover') {
                              setShowDefaultRateTooltip(false);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Seta apontando para o ícone - posição dinâmica */}
                          <div className={`absolute ${tooltipPosition === 'right' ? '-left-2' : '-right-2'} top-2 w-0 h-0 ${
                            tooltipPosition === 'right' 
                              ? 'border-t-8 border-b-8 border-r-8 border-transparent border-r-slate-900'
                              : 'border-t-8 border-b-8 border-l-8 border-transparent border-l-slate-900'
                          }`}></div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-400" />
                                <p className="font-bold text-sm">{getDefaultRateInfo(selectedYear).title}</p>
                              </div>
                              {showDefaultRateTooltip === 'fixed' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDefaultRateTooltip(false);
                                  }}
                                  className="text-slate-400 hover:text-white transition-colors"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                            
                            <div className="space-y-2 bg-slate-800 p-3 rounded-lg">
                              <p className="font-semibold text-xs text-blue-300">📊 Período Analisado:</p>
                              <p className="text-xs text-slate-200 pl-2">
                                {getDefaultRateInfo(selectedYear).period}
                              </p>
                            </div>
                            
                            <div className="space-y-2 bg-slate-800 p-3 rounded-lg">
                              <p className="font-semibold text-xs text-green-300">📋 Como é calculada:</p>
                              <ul className="space-y-1.5 text-xs pl-2">
                                <li className="flex items-start gap-2">
                                  <span className="text-green-400 mt-0.5">•</span>
                                  <span className="text-slate-200">{getDefaultRateInfo(selectedYear).description}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-green-400 mt-0.5">•</span>
                                  <span className="text-slate-200">{getDefaultRateInfo(selectedYear).filter}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-green-400 mt-0.5">•</span>
                                  <span className="text-slate-200"><strong className="text-white">Fórmula:</strong> {getDefaultRateInfo(selectedYear).formula}</span>
                                </li>
                              </ul>
                            </div>
                            
                            <div className="space-y-2 bg-slate-800 p-3 rounded-lg">
                              <p className="font-semibold text-xs text-yellow-300">💡 Regras por Filtro:</p>
                              <ul className="space-y-1.5 text-xs">
                                <li className="flex items-start gap-2 bg-slate-700 p-2 rounded">
                                  <span className="text-green-400 mt-0.5">✓</span>
                                  <div>
                                    <strong className="text-white">Todos os Anos:</strong>
                                    <span className="text-slate-300 block mt-0.5">Últimos 12 meses de todas as parcerias</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-2 bg-slate-700 p-2 rounded">
                                  <span className="text-green-400 mt-0.5">✓</span>
                                  <div>
                                    <strong className="text-white">Ano Atual:</strong>
                                    <span className="text-slate-300 block mt-0.5">Últimos 12 meses do ano atual</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-2 bg-slate-700 p-2 rounded">
                                  <span className="text-blue-400 mt-0.5">✓</span>
                                  <div>
                                    <strong className="text-white">Anos Anteriores:</strong>
                                    <span className="text-slate-300 block mt-0.5">Todas as parcerias do ano fechado</span>
                                  </div>
                                </li>
                              </ul>
                            </div>
                            
                            {showDefaultRateTooltip === 'hover' && (
                              <p className="text-[10px] text-slate-400 text-center italic">
                                Clique no ícone para fixar
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <>
                    <p className="text-3xl lg:text-xl xl:text-2xl font-bold text-slate-900 break-words">
                      {getDefaultRate()}%
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {getDefaultRatePeriodDescription(selectedYear)}
                    </p>
                  </>
                </div>
                <div className="p-3 bg-red-100 rounded-xl flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Status Distribution */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base sm:text-lg font-semibold">Distribuição por Status</CardTitle>
                <ChartHelpButton />
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-80" style={{ minHeight: '256px' }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSION}>
                  <PieChart>
                    <Pie
                      data={getStatusDistribution()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={false}
                      outerRadius={window.innerWidth < 640 ? 80 : 100}
                      fill="#8884d8"
                      dataKey="value"
                      style={{ cursor: 'pointer' }}
                      onClick={(data, index, e) => {
                        const item = getStatusDistribution()[index];
                        if (!item) return;
                        handleChartItemClick('status-distribution', item.name, {
                          title: item.name,
                          rows: [{ label: `${item.value} parcerias (${item.percentage}%)`, color: item.color }]
                        }, e);
                      }}
                    >
                      {getStatusDistribution().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value} parcerias (${props.payload.percentage}%)`,
                        props.payload.name
                      ]}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        fontSize: '12px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legenda customizada - Responsiva */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {getStatusDistribution().map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div 
                      className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-xs sm:text-sm text-slate-700 min-w-0">
                      <strong className="font-semibold">{item.name}:</strong> {item.value} <span className="text-slate-500">({item.percentage}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Value by Year */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-lg font-semibold">Valor por Ano</CardTitle>
                <ChartHelpButton />
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80" style={{ minHeight: '320px' }}>
                <ResponsiveContainer width="100%" height="100%" minHeight={320} initialDimension={CHART_INITIAL_DIMENSION}>
                  <BarChart 
                    data={getValueByYear()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="year" 
                      stroke="#64748b"
                      style={{ fontSize: '14px', fontWeight: '500' }}
                    />
                    <YAxis 
                      stroke="#64748b"
                      tickFormatter={(value) => {
                        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                        return value;
                      }}
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Valor Total']}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px'
                      }}
                      labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#3B82F6"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={60}
                      style={{ cursor: 'pointer' }}
                      onClick={(data, index, e) => {
                        const item = data?.payload;
                        if (!item) return;
                        handleChartItemClick('value-by-year', item.year, {
                          title: `Ano ${item.year}`,
                          rows: [{ label: item.formattedValue, color: '#3B82F6' }]
                        }, e);
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top OSCs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                Top 10 Organizações por Valor
              </CardTitle>
              <ChartHelpButton />
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80 sm:h-96" style={{ minHeight: '320px' }}>
              <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSION}>
                <BarChart 
                  data={getTopOSCs()} 
                  layout="vertical"
                  margin={{ 
                    top: 5, 
                    right: window.innerWidth < 640 ? 10 : 30, 
                    left: window.innerWidth < 640 ? 5 : 10, 
                    bottom: 5 
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    type="number"
                    stroke="#64748b"
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                      return value;
                    }}
                    style={{ fontSize: window.innerWidth < 640 ? '10px' : '12px' }}
                  />
                  <YAxis 
                    dataKey="oscShort" 
                    type="category" 
                    width={window.innerWidth < 640 ? 120 : 220}
                    stroke="#64748b"
                    style={{ fontSize: window.innerWidth < 640 ? '9px' : '11px' }}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const isMobile = window.innerWidth < 640;
                        return (
                          <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: isMobile ? '8px' : '12px',
                            maxWidth: isMobile ? '200px' : '400px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            fontSize: isMobile ? '11px' : '13px'
                          }}>
                            <p style={{ 
                              fontWeight: 'bold', 
                              marginBottom: '6px',
                              fontSize: isMobile ? '11px' : '13px',
                              color: '#1e293b',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: '1.3',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {payload[0].payload.osc}
                            </p>
                            <p style={{ 
                              fontSize: isMobile ? '12px' : '14px',
                              color: '#10B981',
                              fontWeight: '600'
                            }}>
                              {formatCurrency(payload[0].value)}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#10B981"
                    radius={[0, 8, 8, 0]}
                    maxBarSize={window.innerWidth < 640 ? 25 : 35}
                    style={{ cursor: 'pointer' }}
                    onClick={(data, index, e) => {
                      const item = data?.payload;
                      if (!item) return;
                      handleChartItemClick('top-oscs', item.osc, {
                        title: item.osc,
                        rows: [{ label: item.formattedValue, color: '#10B981' }]
                      }, e);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Novos Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Parcerias por Tipo */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                  Parcerias por Tipo
                </CardTitle>
                <ChartHelpButton />
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-72" style={{ minHeight: '256px' }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSION}>
                  <BarChart 
                    data={getPartnershipsByType()} 
                    layout="vertical"
                    margin={{ 
                      top: 5, 
                      right: window.innerWidth < 640 ? 10 : 20, 
                      left: window.innerWidth < 640 ? 5 : 10, 
                      bottom: 5 
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      type="number" 
                      stroke="#64748b" 
                      style={{ fontSize: window.innerWidth < 640 ? '10px' : '12px' }} 
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={window.innerWidth < 640 ? 100 : 140}
                      stroke="#64748b"
                      style={{ fontSize: window.innerWidth < 640 ? '9px' : '10px' }}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value} parcerias`, 'Total']}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: window.innerWidth < 640 ? '8px' : '12px',
                        fontSize: window.innerWidth < 640 ? '11px' : '13px'
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#6366f1"
                      radius={[0, 8, 8, 0]}
                      maxBarSize={window.innerWidth < 640 ? 20 : 25}
                      style={{ cursor: 'pointer' }}
                      onClick={(data, index, e) => {
                        const item = data?.payload;
                        if (!item) return;
                        handleChartItemClick('partnerships-by-type', item.name, {
                          title: item.name,
                          rows: [{ label: `${item.value} parcerias`, color: '#6366f1' }]
                        }, e);
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Parcerias por Origem */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
                  Parcerias por Origem
                </CardTitle>
                <ChartHelpButton />
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64 sm:h-72" style={{ minHeight: '256px' }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSION}>
                  <PieChart>
                    <Pie
                      data={getPartnershipsByOrigin()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={false}
                      outerRadius={window.innerWidth < 640 ? 70 : 80}
                      fill="#8884d8"
                      dataKey="value"
                      style={{ cursor: 'pointer' }}
                      onClick={(data, index, e) => {
                        const item = getPartnershipsByOrigin()[index];
                        if (!item) return;
                        handleChartItemClick('partnerships-by-origin', item.name, {
                          title: item.name,
                          rows: [{ label: `${item.value} parcerias (${item.percentage}%)`, color: COLORS[index % COLORS.length] }]
                        }, e);
                      }}
                    >
                      {getPartnershipsByOrigin().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value} parcerias (${props.payload.percentage}%)`,
                        props.payload.name
                      ]}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: window.innerWidth < 640 ? '8px' : '12px',
                        fontSize: window.innerWidth < 640 ? '11px' : '13px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legenda para mobile */}
              <div className="mt-3 grid grid-cols-1 gap-2">
                {getPartnershipsByOrigin().map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs sm:text-sm">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-slate-700 truncate">
                      <strong>{item.name}:</strong> {item.value} ({item.percentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top 5 Locais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-rose-600" />
                Top 5 Locais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {getTopLocations().map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 truncate flex-1">
                        {item.location}
                      </span>
                      <span className="text-sm font-bold text-slate-900 ml-2">
                        {item.count}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500">{item.percentage}% do total</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Valor por Tipo de Parceria */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Valor por Tipo de Parceria
              </CardTitle>
              <ChartHelpButton />
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72 sm:h-96" style={{ minHeight: '288px' }}>
              {(() => {
                const sankeyData = getValueByTypeSankeyData();
                // Sem links ainda (parcerias não carregaram / filtro sem resultado): o algoritmo
                // de layout do Sankey precisa dividir pelo valor total para posicionar os nós —
                // com zero links esse total é 0, e 0/0 = NaN. Não renderizar até haver dado real
                // evita esse cálculo inválido em vez de deixar o gráfico gerar coordenadas NaN.
                if (sankeyData.links.length === 0) {
                  return (
                    <div className="h-full flex items-center justify-center text-sm text-slate-500">
                      {isLoading ? 'Carregando dados...' : 'Nenhum dado disponível para exibir.'}
                    </div>
                  );
                }
                return (
                  <ResponsiveContainer width="100%" height="100%" initialDimension={CHART_INITIAL_DIMENSION}>
                    <Sankey
                      data={sankeyData}
                      node={renderValueByTypeSankeyNode}
                      link={renderValueByTypeSankeyLink}
                      nodePadding={30}
                      nodeWidth={12}
                      margin={{ top: 20, right: window.innerWidth < 640 ? 90 : 160, bottom: 20, left: window.innerWidth < 640 ? 50 : 70 }}
                      onClick={(item, type, e) => {
                        if (type !== 'link') return;
                        const targetName = item.payload?.target?.name;
                        const targetValue = item.payload?.value;
                        if (!targetName) return;
                        const index = getValueByType().findIndex((t) => t.name === targetName);
                        handleChartItemClick('value-by-type', targetName, {
                          title: targetName,
                          rows: [{ label: formatCurrency(targetValue), color: COLORS[Math.max(index, 0) % COLORS.length] }]
                        }, e);
                      }}
                    />
                  </ResponsiveContainer>
                );
              })()}
            </div>
            {/* Legenda com nome completo + valor de cada categoria (mesma cor do fluxo no diagrama) */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {getValueByType().map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <div
                    className="w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-xs sm:text-sm text-slate-700 min-w-0 break-words">
                    <strong className="font-semibold">{item.name}:</strong> {formatCurrency(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Balão de dados por clique — compartilhado entre todos os gráficos da página */}
      {chartTooltip && (
        <div
          className="fixed z-[200] bg-white border border-slate-200 rounded-lg shadow-2xl p-3 text-sm max-w-[260px] pointer-events-none"
          style={{
            left: Math.min(chartTooltip.x + 14, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 260),
            top: Math.max(chartTooltip.y - 14, 12),
          }}
        >
          <p className="font-semibold text-slate-900 mb-1 break-words">{chartTooltip.title}</p>
          {chartTooltip.rows.map((row, i) => (
            <p key={i} className="text-slate-700 flex items-center gap-1.5 break-words">
              {row.color && (
                <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: row.color }} />
              )}
              {row.label}
            </p>
          ))}
          <p className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
            {chartTooltip.pinned
              ? '📌 Fixado — clique de novo no item para fechar'
              : 'Clique de novo no item para fixar (fecha em 6s)'}
          </p>
        </div>
      )}
    </div>
  );
}
